import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Globe, Navigation, ArrowUpDown, Search, X, Plus, Stethoscope } from "lucide-react";
import { stateAbbreviations } from "@/components/USAMapNavigator";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CreateScenarioDialog } from "@/components/CreateScenarioDialog";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import { withScenario } from "@/lib/mapNav";
import { getHospitalGrade } from "@/lib/grades";
import type { Hospital, ScenarioPricing } from "@/types/map";

interface MapLeftPanelProps {
  // Brand
  brand: any;
  isAhp: boolean;
  panelBg: string;
  accentBgClass: string;
  logoBorderClass: string;

  // Debug
  debugToggles: { leftPanel: boolean };

  // Navigation
  flyToState: (stateCode: string) => void;
  navigate: (path: string, options?: any) => void;
  setSelectedTableState: (s: string) => void;
  setSelectedTableCity: (s: string) => void;
  setSelectedHospital: (h: Hospital | null) => void;

  // State search
  stateSearch: string;
  setStateSearch: (s: string) => void;
  showStateDropdown: boolean;
  setShowStateDropdown: (v: boolean) => void;

  // GPS
  findNearMe: () => void;
  isLocatingGps: boolean;
  nearbyHospitals: Array<Hospital & { distance: number }>;
  userLocation: { lat: number; lng: number } | null;

  // Nearby filters
  showEROnly: boolean;
  setShowEROnly: (v: boolean) => void;
  showUrgentCareOnly: boolean;
  setShowUrgentCareOnly: (v: boolean) => void;
  sortByMarkup: 'asc' | 'desc' | 'none';
  setSortByMarkup: (v: 'asc' | 'desc' | 'none') => void;

  // Hospital selection
  fetchHospitalProcedures: (hospitalId: string) => void;
  orbitHospital: (hospital: Hospital) => void;

  // Scenario
  activeScenarioId: string | null;
  setActiveScenarioId: (v: string | null) => void;
  scenarioHospitalPricing: ScenarioPricing;
  setSearchParams: (v: any) => void;
}

export const MapLeftPanel: React.FC<MapLeftPanelProps> = ({
  brand,
  isAhp,
  panelBg,
  accentBgClass,
  logoBorderClass,
  debugToggles,
  flyToState,
  navigate,
  setSelectedTableState,
  setSelectedTableCity,
  setSelectedHospital,
  stateSearch,
  setStateSearch,
  showStateDropdown,
  setShowStateDropdown,
  findNearMe,
  isLocatingGps,
  nearbyHospitals,
  userLocation,
  showEROnly,
  setShowEROnly,
  showUrgentCareOnly,
  setShowUrgentCareOnly,
  sortByMarkup,
  setSortByMarkup,
  fetchHospitalProcedures,
  orbitHospital,
  activeScenarioId,
  setActiveScenarioId,
  scenarioHospitalPricing,
  setSearchParams,
}) => {
  const { t } = useTranslation('map');
  const [scenarioSearch, setScenarioSearch] = useState("");
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const ahpHeaderOffset = useAhpHeaderOffset();

  // Fetch all active scenarios for the selector
  const { data: scenarios = [] } = useQuery({
    queryKey: ['map-scenarios-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scenarios')
        .select('id, name, category, icon_name, estimated_cost, total_procedures')
        .eq('is_active', true)
        .order('category')
        .order('name');
      if (error) { console.error('Scenarios fetch error:', error); return []; }
      return data || [];
    },
  });

  // Group scenarios by category
  const groupedScenarios = scenarios.reduce((acc: Record<string, typeof scenarios>, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const filteredScenarios = scenarioSearch
    ? scenarios.filter(s => s.name.toLowerCase().includes(scenarioSearch.toLowerCase()) || (s.category || '').toLowerCase().includes(scenarioSearch.toLowerCase()))
    : scenarios;

  const filteredGrouped = filteredScenarios.reduce((acc: Record<string, typeof scenarios>, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const activeScenario = scenarios.find(s => s.id === activeScenarioId);

  return (
    <>
    <CreateScenarioDialog
      open={showCreateDialog}
      onOpenChange={setShowCreateDialog}
      onScenarioCreated={(id) => {
        setActiveScenarioId(id);
        setSearchParams({ scenario: id });
        setShowCreateDialog(false);
      }}
    />
    <div
      className={`w-full lg:max-w-[400px] border-r border-border flex flex-col overflow-y-auto relative ${isAhp ? 'ahp-dark-panel' : ''}`}
      style={{ background: panelBg, height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : "100vh", zIndex: 100, display: debugToggles.leftPanel ? '' : 'none' }}
    >
      <div className="p-6 border-b border-border space-y-2">
      <div className="mb-4 flex justify-center">
        {isAhp ? (
          <div className="text-center py-3 px-4">
            <div style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: '1.65rem', lineHeight: 1.15, color: 'var(--civic-cream)', letterSpacing: '-0.01em' }}>
              American<br />Hospital Prices
            </div>
            <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: 'var(--civic-gold)', marginTop: '6px' }}>
              Compare Real Prices
            </div>
          </div>
        ) : (
          <img
            src={brand.key === 'antigreed' ? "/antigreed-guide-logo.svg" : "/HoSLogo.png"}
            alt={brand.key === 'antigreed' ? "The Anti-Greed Guide" : "Hall of Shame"}
            className={`w-full max-w-[280px] h-auto ${logoBorderClass}`}
            style={{
              borderRadius: '8px',
              padding: '12px'
            }}
          />
        )}
      </div>

      {/* Navigation Controls */}
      <div className="space-y-3 mt-4">
        {/* USA View Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            flyToState('USA');
            setSelectedTableState('USA');
            setSelectedTableCity('');
            setSelectedHospital(null);
            navigate(withScenario('/map'), { replace: true });
          }}
          className="w-full"
        >
          <Globe className="w-4 h-4 mr-2" />
          {t('navigation.flyToState')}
        </Button>

        {/* State Selector - Typeahead */}
        <div className="relative">
          <Input
            placeholder={t('filters.searchStates')}
            value={stateSearch}
            onChange={(e) => {
              setStateSearch(e.target.value);
              setShowStateDropdown(true);
            }}
            onFocus={() => setShowStateDropdown(true)}
            onBlur={() => setTimeout(() => setShowStateDropdown(false), 200)}
            className="w-full"
          />
          {showStateDropdown && (
            <div className="absolute top-full mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
              {Object.entries(stateAbbreviations)
                .filter(([stateName]) =>
                  stateName.toLowerCase().includes(stateSearch.toLowerCase())
                )
                .map(([stateName, stateCode]) => (
                  <button
                    key={stateCode}
                    onClick={() => {
                      flyToState(stateCode);
                      setSelectedTableState(stateCode);
                      setSelectedTableCity('');
                      setSelectedHospital(null);
                      navigate(withScenario(`/map/${stateCode.toLowerCase()}`), { replace: true });
                      setStateSearch('');
                      setShowStateDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border/30 last:border-b-0"
                  >
                    <div className="text-sm text-foreground">{stateName}</div>
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Find Near Me Button */}
        <Button
          variant="default"
          size="sm"
          onClick={findNearMe}
          className={`w-full ${accentBgClass}`}
        >
          <Navigation className="w-4 h-4 mr-2" />
          {t('navigation.findNearMe')}
        </Button>
      </div>

      {/* Scenario Selector */}
      <div className="mt-4 pt-4 border-t border-border/30">
        <div className="flex items-center justify-between mb-2">
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${isAhp ? 'text-civic-gold' : 'text-muted-foreground'}`}>
            Price a Scenario
          </h3>
          {activeScenarioId && (
            <button
              onClick={() => {
                setActiveScenarioId(null);
                setSearchParams({});
              }}
              className={`text-xs flex items-center gap-1 ${isAhp ? 'text-white/50 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>

        {activeScenarioId && activeScenario ? (
          <div className={`p-2.5 rounded-lg border text-sm ${isAhp ? 'bg-civic-gold/10 border-civic-gold/30 text-white' : 'bg-primary/10 border-primary/30 text-foreground'}`}>
            <div className="flex items-center gap-2">
              <Stethoscope className={`w-4 h-4 shrink-0 ${isAhp ? 'text-civic-gold' : 'text-primary'}`} />
              <span className="font-medium truncate">{activeScenario.name}</span>
            </div>
            {activeScenario.estimated_cost && (
              <div className={`text-xs mt-1 ml-6 ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>
                National avg: ${Number(activeScenario.estimated_cost).toLocaleString()}
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isAhp ? 'text-white/40' : 'text-muted-foreground'}`} />
              <Input
                placeholder={t('filters.searchScenarios', { defaultValue: 'Search scenarios (knee surgery, childbirth…)' })}
                value={scenarioSearch}
                onChange={(e) => {
                  setScenarioSearch(e.target.value);
                  setShowScenarioDropdown(true);
                }}
                onFocus={() => setShowScenarioDropdown(true)}
                onBlur={() => setTimeout(() => setShowScenarioDropdown(false), 200)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            {showScenarioDropdown && (
              <div className="absolute top-full mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                {Object.keys(filteredGrouped).length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground text-center">No scenarios found</div>
                ) : (
                  Object.entries(filteredGrouped).map(([category, items]) => (
                    <div key={category}>
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/50 sticky top-0">
                        {category}
                      </div>
                      {items.map((scenario) => (
                        <button
                          key={scenario.id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setActiveScenarioId(scenario.id);
                            setSearchParams({ scenario: scenario.id });
                            setScenarioSearch('');
                            setShowScenarioDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border/20 last:border-b-0"
                        >
                          <div className="text-sm text-foreground truncate">{scenario.name}</div>
                          {scenario.estimated_cost && (
                            <div className="text-xs text-muted-foreground">
                              ~${Number(scenario.estimated_cost).toLocaleString()}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ))
                )}
                {/* Create New CTA */}
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setShowScenarioDropdown(false);
                    setShowCreateDialog(true);
                  }}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-t border-border ${isAhp ? 'text-civic-gold hover:bg-civic-gold/10' : 'text-primary hover:bg-primary/10'}`}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Create New Scenario</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GPS Loading Animation */}
      {isLocatingGps && nearbyHospitals.length === 0 && (
        <div className="mt-6 flex flex-col items-center gap-4 py-8">
          {/* Pulsing pin animation */}
          <div className="relative">
            <div className={`w-16 h-16 rounded-full ${isAhp ? 'bg-civic-gold/10' : 'bg-matrix-green/10'} flex items-center justify-center`}>
              <div className={`absolute inset-0 rounded-full ${isAhp ? 'bg-civic-gold/5' : 'bg-matrix-green/5'} animate-ping`} />
              <Navigation className={`w-7 h-7 ${isAhp ? 'text-civic-gold' : 'text-matrix-green'} animate-pulse`} />
            </div>
          </div>
          <div className="text-center space-y-1">
            <p className={`text-sm font-medium ${isAhp ? 'text-white' : 'text-foreground'}`}>
              {t('navigation.locating')}
            </p>
            <p className={`text-xs ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>
              {t('panels.left.nearbyHospitals')}
            </p>
          </div>
          {/* Skeleton cards */}
          <div className="w-full space-y-2 mt-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`p-3 border rounded-lg ${isAhp ? 'bg-[var(--civic-navy-deep)]/30 border-civic-gold/10' : 'bg-background/30 border-border/20'}`}
                style={{ opacity: 1 - i * 0.25, animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite` }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-2">
                    <div className={`h-3.5 rounded ${isAhp ? 'bg-white/10' : 'bg-foreground/10'}`} style={{ width: `${70 - i * 10}%` }} />
                    <div className={`h-2.5 rounded ${isAhp ? 'bg-white/5' : 'bg-foreground/5'}`} style={{ width: '40%' }} />
                  </div>
                  <div className={`h-3 w-12 rounded ${isAhp ? 'bg-civic-gold/10' : 'bg-blue-400/10'}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nearby Hospitals Section */}
      {nearbyHospitals.length > 0 && (
        <div className="mt-4">
          <h2 className={`text-sm font-semibold mb-3 ${isAhp ? 'text-white' : 'text-foreground'}`}>{t('panels.left.nearbyHospitals')}</h2>

          {/* Filters */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="er-filter"
                  checked={showEROnly}
                  onCheckedChange={(checked) => setShowEROnly(checked as boolean)}
                />
                <label htmlFor="er-filter" className={`text-xs cursor-pointer ${isAhp ? 'text-white/80' : 'text-foreground'}`}>
                  {t('panels.left.showEROnly')}
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="urgent-filter"
                  checked={showUrgentCareOnly}
                  onCheckedChange={(checked) => setShowUrgentCareOnly(checked as boolean)}
                />
                <label htmlFor="urgent-filter" className={`text-xs cursor-pointer ${isAhp ? 'text-white/80' : 'text-foreground'}`}>
                  {t('panels.left.showUrgentCareOnly')}
                </label>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSortByMarkup(sortByMarkup === 'asc' ? 'desc' : sortByMarkup === 'desc' ? 'none' : 'asc');
              }}
              className="w-full text-xs h-8"
            >
              <ArrowUpDown className="w-3 h-3 mr-2" />
              {t('panels.left.sortByMarkup')} {sortByMarkup === 'asc' ? '\u2191' : sortByMarkup === 'desc' ? '\u2193' : ''}
            </Button>
          </div>

          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {nearbyHospitals
              .filter(hospital => {
                if (showEROnly && !hospital.name.toLowerCase().includes('emergency')) {
                  return false;
                }
                if (showUrgentCareOnly && !hospital.name.toLowerCase().includes('urgent')) {
                  return false;
                }
                return true;
              })
              .sort((a, b) => {
                // Hospital-level metric = median-baseline markup (rank basis)
                const metric = (h: typeof a) => h.median_markup_percentage ?? h.avg_markup_percentage ?? 0;
                if (sortByMarkup === 'asc') {
                  return metric(a) - metric(b);
                } else if (sortByMarkup === 'desc') {
                  return metric(b) - metric(a);
                }
                return 0; // Keep original distance-based order
              })
              .map((hospital) => (
              <button
                key={hospital.id}
                onClick={() => {
                  setSelectedHospital(hospital);
                  fetchHospitalProcedures(hospital.id);
                  orbitHospital(hospital);
                  navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
                }}
                className={`w-full text-left p-3 border rounded-lg transition-all ${isAhp ? 'bg-[var(--civic-navy-deep)]/40 hover:bg-[var(--civic-navy-deep)]/70 border-civic-gold/15' : 'bg-background/50 hover:bg-accent border-border/30'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm truncate ${isAhp ? 'text-white' : 'text-foreground'}`}>{hospital.name}</div>
                    <div className={`text-xs ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>{hospital.city}, {hospital.state}</div>
                  </div>
                  <div className={`text-xs font-mono whitespace-nowrap ${isAhp ? 'text-civic-gold' : 'text-blue-400'}`}>
                    {t('panels.left.distance', { distance: hospital.distance.toFixed(1) })}
                  </div>
                </div>
                <div className="flex gap-3 mt-2 text-xs">
                  {activeScenarioId && scenarioHospitalPricing.has(hospital.id) ? (
                    <>
                      <div className="flex items-center gap-1">
                        <span className={isAhp ? 'text-white/50' : 'text-muted-foreground'}>{t('tooltip.scenarioPrice')}:</span>
                        <span className="font-semibold text-foreground font-mono">
                          ${scenarioHospitalPricing.get(hospital.id)!.blendedPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={isAhp ? 'text-white/50' : 'text-muted-foreground'}>{t('tooltip.avgMarkup')}:</span>
                        <span className={`font-semibold ${
                          scenarioHospitalPricing.get(hospital.id)!.blendedMarkup >= 100 ? 'text-red-500' :
                          scenarioHospitalPricing.get(hospital.id)!.blendedMarkup >= 50 ? 'text-orange-500' :
                          scenarioHospitalPricing.get(hospital.id)!.blendedMarkup >= 0 ? 'text-yellow-500' :
                          'text-green-500'
                        }`}>
                          {scenarioHospitalPricing.get(hospital.id)!.blendedMarkup > 0 ? '+' : ''}
                          {scenarioHospitalPricing.get(hospital.id)!.blendedMarkup.toFixed(0)}%
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1">
                        <span className={isAhp ? 'text-white/50' : 'text-muted-foreground'}>{t('tooltip.markupVsMedian', { defaultValue: 'Markup' })}:</span>
                        {(() => {
                          // Hospital-level metric + hospital grade scale
                          const m = hospital.median_markup_percentage ?? hospital.avg_markup_percentage ?? 0;
                          const g = getHospitalGrade(m);
                          const cls = g === 'F' ? 'text-red-500' : g === 'D' ? 'text-orange-500' : g === 'C' ? 'text-yellow-500' : 'text-green-500';
                          return <span className={`font-semibold ${cls}`}>{m.toFixed(1)}%</span>;
                        })()}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={isAhp ? 'text-white/50' : 'text-muted-foreground'}>{t('tooltip.outlierRate')}:</span>
                        <span className={`font-semibold ${
                          (hospital.outlier_percentage || 0) >= 40 ? 'text-red-500' :
                          (hospital.outlier_percentage || 0) >= 25 ? 'text-orange-500' :
                          (hospital.outlier_percentage || 0) >= 10 ? 'text-yellow-500' :
                          'text-green-500'
                        }`}>
                          {hospital.outlier_percentage?.toFixed(1) || '0.0'}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );
};

export default MapLeftPanel;
